import { useMemo } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { Dialog } from '@components/Dialog/Dialog';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { DatePicker } from '@components/datePicker/DatePicker';
import { Button } from '@components/buttons/Button';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { useCreateScimToken } from '@queryHooks/useScimToken';

type Props = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
};

type ScimTokenFormData = {
  description: string;
  expiresAt: dayjs.Dayjs | null;
};

export const ScimTokenCreateModal = ({ open, onClose, tenantId }: Props) => {
  const { t } = useTranslation();
  const {
    uiStore: { toastStore },
  } = useStore();

  const { mutate: createToken, isLoading } = useCreateScimToken();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        description: Yup.string().required(t('ScimToken.create.descriptionRequired')).max(255).trim(),
        expiresAt: Yup.mixed<dayjs.Dayjs | null>()
          .required(t('ScimToken.create.expiresAtRequired'))
          .test('isValidDate', t('ScimToken.create.invalidDate'), value => {
            if (!value) return false;
            return dayjs(value).isValid() && dayjs(value).isAfter(dayjs());
          }),
      }),
    [t],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ScimTokenFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      description: '',
      expiresAt: dayjs().add(1, 'year'),
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleValid: SubmitHandler<ScimTokenFormData> = formData => {
    createToken(
      {
        tenantId,
        dto: {
          description: formData.description.trim(),
          expiresAt: dayjs(formData.expiresAt).format('YYYY-MM-DDTHH:mm:ssZ'),
        },
      },
      {
        onSuccess: () => {
          toastStore.open(t('ScimToken.create.success'), 'bottom', 'center');
          handleClose();
        },
        onError: () => {
          toastStore.open(t('ScimToken.create.fail'), 'bottom', 'center');
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} title={t('ScimToken.create.title')} size="medium" useCustomContent>
      <FormBody onSubmit={handleSubmit(handleValid)}>
        <FieldsContainer>
          <FieldRow>
            <RequiredLabel>{t('ScimToken.create.description')}</RequiredLabel>
            <TextField
              {...register('description')}
              placeholder={t('ScimToken.create.descriptionPlaceholder')}
              size="small"
              fullWidth
              error={!!errors.description}
              helperText={errors.description?.message}
            />
          </FieldRow>
          <FieldRow>
            <RequiredLabel>{t('ScimToken.create.expiresAt')}</RequiredLabel>
            <Controller
              control={control}
              name="expiresAt"
              render={({ field: { ref, value, onChange } }) => (
                <DatePicker
                  inputRef={ref}
                  value={value}
                  onChange={onChange}
                  minDate={dayjs().add(1, 'day')}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={!!errors.expiresAt}
                      helperText={errors.expiresAt?.message}
                    />
                  )}
                />
              )}
            />
          </FieldRow>
        </FieldsContainer>
        <ButtonBox>
          <SecureSubmitButton isPending={isLoading} type="submit" variant="contain" size="extraLarge">
            {t('ScimToken.create.submit')}
          </SecureSubmitButton>
          <Button variant="outline" size="extraLarge" onClick={handleClose}>
            {t('Cancel')}
          </Button>
        </ButtonBox>
      </FormBody>
    </Dialog>
  );
};

const FormBody = styled.form`
  display: flex;
  flex-direction: column;
  padding: 12px 28px 28px;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RequiredLabel = styled(Typography)`
  ${fonts.Body4};
  color: ${({ theme }) => theme.colors['text-gray-main']};
  &::after {
    content: ' *';
    color: ${({ theme }) => theme.colors['text-red-light']};
  }
` as typeof Typography;

const ButtonBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;

  & > button {
    flex: 1;
  }
`;
